import { useRef, useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { EmailVariablesSelector, EMAIL_VARIABLES } from "@/components/email-variables-selector";
import { CcEmailInput } from "@/components/cc-email-input";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { useToast } from "@/hooks/use-toast";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { useMyTier } from "@/hooks/use-my-tier";
import { Loader2, Send, Mail, Clock, ShieldCheck, CheckCircle2, XCircle, AlertTriangle, HelpCircle, RefreshCw } from "lucide-react";
import { VoiceToTextButton } from "@/components/voice-field";
import confetti from "canvas-confetti";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";

const triggerConfetti = () => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999,
  };
  function fire(particleRatio: number, opts: confetti.Options) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
    });
  }
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

interface ConfirmationDraft {
  noTemplateFound: boolean;
  matchType?: 'exact' | 'eventOffering' | 'clientType' | 'default' | 'none';
  templateId?: string;
  to: string;
  recipientName: string;
  subject?: string;
  bodyHtml?: string;
  allTemplates?: Array<{ id: string; clientType: string; subject: string; body: string; eventOfferingId?: string | null }>;
  searchCriteria?: {
    clientType?: string;
    eventOfferingId?: string;
  };
}

interface ConfirmationTemplate {
  id: string;
  clientType: string;
  subject: string;
  body: string;
  eventOfferingId?: string | null;
  talentId?: string | null;
}

interface EmailComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  onEmailSent?: () => void;
}

export function EmailComposerModal({ isOpen, onClose, invoiceId, onEmailSent }: EmailComposerModalProps) {
  const { toast } = useToast();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const editorRef = useRef<RichTextEditorHandle>(null);
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [noTemplateFound, setNoTemplateFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [matchType, setMatchType] = useState<ConfirmationDraft['matchType']>(undefined);
  const [searchCriteria, setSearchCriteria] = useState<ConfirmationDraft['searchCriteria']>(undefined);
  const [allTemplates, setAllTemplates] = useState<ConfirmationTemplate[]>([]);

  // Task #244 — manual single-send NeverBounce verification is a tier perk.
  // Only show the on-demand "Verify" affordance when the workspace has it on.
  const { data: myTier } = useMyTier();
  const verifyPerkEnabled = myTier?.neverbounceManualEnabled === true;
  const [verifiedResult, setVerifiedResult] = useState<{ email: string; result: string } | null>(null);

  const verifyRecipient = useMutation({
    mutationFn: (email: string) =>
      apiRequest("POST", "/api/email/verify-recipient", { email }).then((r) => r.json()),
    onSuccess: (data: { email: string; result: string }) => {
      setVerifiedResult(data);
      const meta: Record<string, { variant: "default" | "destructive"; label: string }> = {
        valid: { variant: "default", label: "Deliverable mailbox" },
        catchall: { variant: "default", label: "Catch-all domain (accepted)" },
        invalid: { variant: "destructive", label: "Invalid / dead mailbox" },
        disposable: { variant: "destructive", label: "Disposable address" },
        unknown: { variant: "default", label: "Could not confirm — send with caution" },
      };
      const m = meta[data.result] || meta.unknown;
      toast({ title: `Verification: ${data.result}`, description: m.label, variant: m.variant });
    },
    onError: (err: any) => {
      toast({ title: "Verification failed", description: err?.message || "Try again.", variant: "destructive" });
    },
  });

  useEffect(() => {
    if (isOpen && invoiceId) {
      loadDraft();
    }
  }, [isOpen, invoiceId]);

  const getMatchTypeMessage = () => {
    if (!matchType || matchType === 'exact') return null;
    
    const criteria = [];
    if (searchCriteria?.clientType) criteria.push(`"${searchCriteria.clientType}" client type`);
    if (searchCriteria?.eventOfferingId) criteria.push("show offering");
    
    switch (matchType) {
      case 'eventOffering':
        return `Using template matched by show offering. No exact match for ${criteria.join(' + ')}.`;
      case 'clientType':
        return `Using template matched by client type. No exact match for ${criteria.join(' + ')}.`;
      case 'default':
        return `Using default template. No template found matching ${criteria.join(' or ')}.`;
      case 'none':
        return `No templates found. Please compose your email or select a template.`;
      default:
        return null;
    }
  };

  const loadDraft = async () => {
    setIsLoadingDraft(true);
    setNoTemplateFound(false);
    setLoadFailed(false);
    try {
      const response = await apiRequest("GET", `/api/invoices/${invoiceId}/confirmation-draft`);
      const draft: ConfirmationDraft = await response.json();
      
      setTo(draft.to || "");
      setMatchType(draft.matchType);
      setSearchCriteria(draft.searchCriteria);

      // Seed the template dropdown from the draft response so we don't fire a
      // separate GET /api/confirmation-email-templates round-trip.
      if (draft.allTemplates) {
        setAllTemplates(draft.allTemplates as ConfirmationTemplate[]);
      }
      
      if (draft.noTemplateFound) {
        setNoTemplateFound(true);
        setSubject("");
        setBody("");
      } else {
        setSubject(draft.subject || "");
        setBody(draft.bodyHtml || "");
        setSelectedTemplateId(draft.templateId || null);
      }
    } catch (error: any) {
      console.error("Error loading confirmation draft:", error);
      setLoadFailed(true);
    } finally {
      setIsLoadingDraft(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    if (templateId === "none") {
      setSelectedTemplateId(null);
      return;
    }
    const template = allTemplates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject || "");
      setBody(template.body || "");
      setSelectedTemplateId(templateId);
    }
  };

  const sendEmail = useSendEmailWithPreflight();
  const [isSending, setIsSending] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  const handleSend = async () => {
    if (!to) {
      toast({ title: "Error", description: "Recipient email is required", variant: "destructive" });
      return;
    }
    if (!subject) {
      toast({ title: "Error", description: "Subject is required", variant: "destructive" });
      return;
    }
    if (!body) {
      toast({ title: "Error", description: "Email body is required", variant: "destructive" });
      return;
    }

    // Snapshot the values, then drop the dialog immediately so the user can
    // keep working. The preflight + send (or draft-hold) happens in the
    // background and surfaces success/queue toasts when done.
    const payload = {
      to,
      subject,
      body,
      cc: cc.trim() || undefined,
      attachments: attachments.length > 0
        ? attachments.map((a) => ({
            filename: a.filename,
            contentBase64: a.contentBase64,
            contentType: a.contentType,
          }))
        : undefined,
    };
    const recipient = to;
    const sub = subject;
    onClose();
    setIsSending(true);

    await sendEmail(
      {
        endpoint: `/api/email/confirmation/${invoiceId}`,
        payload,
        recipientEmail: recipient,
        subject: sub,
        description: "booking confirmation",
      },
      {
        onSent: () => {
          triggerConfetti();
          toast({
            title: "Confirmation Sent!",
            description: `Email sent to ${recipient}`,
          });
          queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
          onEmailSent?.();
        },
        onQueued: () => {
          // Draft is held; toast handled by hook. Still notify the booking flow.
          onEmailSent?.();
        },
        onError: (error: any) => {
          toast({
            title: "Error",
            description: error?.message || "Failed to send confirmation email",
            variant: "destructive",
          });
        },
      },
    );
    setIsSending(false);
  };

  const handleClose = () => {
    setTo("");
    setCc("");
    setSubject("");
    setBody("");
    setAttachments([]);
    setSelectedTemplateId(null);
    setMatchType(undefined);
    setSearchCriteria(undefined);
    setAllTemplates([]);
    setNoTemplateFound(false);
    setLoadFailed(false);
    onClose();
  };

  const handleInsertVariable = (variable: string) => {
    if (editorRef.current) {
      editorRef.current.insertAtCursor(variable);
    } else {
      setBody((prev) => prev + variable);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-screen h-[100dvh] max-w-full sm:w-full sm:h-auto sm:max-w-2xl sm:max-h-[85vh] rounded-none sm:rounded-lg flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Booking Confirmation
          </DialogTitle>
          <DialogDescription>
            Review and customize the confirmation email before sending.
          </DialogDescription>
        </DialogHeader>

        {isLoadingDraft ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto pr-2">
            {loadFailed && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md text-sm" data-testid="template-load-failed-notice">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-amber-800 dark:text-amber-300 flex-1">Confirmation template couldn't be loaded. You can compose the email manually below.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={loadDraft}
                  disabled={isLoadingDraft}
                  data-testid="button-retry-load-draft"
                  className="shrink-0 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              </div>
            )}
            {!loadFailed && noTemplateFound && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md text-sm" data-testid="template-not-found-notice">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <span className="text-amber-800 dark:text-amber-300">No confirmation template was found for this client type. Compose your email below or add a template in Communications → Confirmation Emails.</span>
              </div>
            )}
            {!loadFailed && !noTemplateFound && matchType && matchType !== 'exact' && (
              <div className="flex items-center gap-2 p-3 bg-muted/50 border rounded-md text-sm" data-testid="template-match-info">
                <Badge variant="outline" className="text-xs shrink-0">
                  {matchType === 'eventOffering' ? 'Show Match' : 
                   matchType === 'clientType' ? 'Client Type Match' : 
                   matchType === 'default' ? 'Default' : 'No Match'}
                </Badge>
                <span className="text-muted-foreground">{getMatchTypeMessage()}</span>
              </div>
            )}
            
            <div className="grid gap-2">
              <Label htmlFor="to">To</Label>
              <div className="flex gap-2">
                <Input
                  id="to"
                  type="email"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); setVerifiedResult(null); }}
                  placeholder="Enter recipient email..."
                  data-testid="input-email-to"
                  className="flex-1"
                />
                {verifyPerkEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => verifyRecipient.mutate(to.trim())}
                    disabled={!to.includes("@") || verifyRecipient.isPending}
                    data-testid="button-verify-recipient"
                    className="shrink-0"
                  >
                    {verifyRecipient.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4" />
                    )}
                    <span className="ml-1.5 hidden sm:inline">Verify</span>
                  </Button>
                )}
              </div>
              {verifyPerkEnabled && verifiedResult && verifiedResult.email === to.trim().toLowerCase() && (
                <Badge
                  variant={["invalid", "disposable"].includes(verifiedResult.result) ? "destructive" : "secondary"}
                  className="w-fit gap-1 font-normal"
                  data-testid="badge-verify-result"
                >
                  {verifiedResult.result === "valid" && <CheckCircle2 className="h-3 w-3" />}
                  {verifiedResult.result === "catchall" && <AlertTriangle className="h-3 w-3" />}
                  {["invalid", "disposable"].includes(verifiedResult.result) && <XCircle className="h-3 w-3" />}
                  {verifiedResult.result === "unknown" && <HelpCircle className="h-3 w-3" />}
                  {verifiedResult.result}
                </Badge>
              )}
            </div>

            <CcEmailInput
              value={cc}
              onChange={setCc}
              data-testid="input-email-cc"
            />

            <div className="grid gap-2">
              <Label htmlFor="template-select">Template</Label>
              <Select
                value={selectedTemplateId || "none"}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger data-testid="select-email-template">
                  <SelectValue placeholder="Select a template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {allTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.clientType || "General"} - {template.subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject..."
                data-testid="input-email-subject"
              />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <div className="flex items-center gap-2">
                  <VoiceToTextButton onTranscript={(t) => editorRef.current?.insertAtCursor(t)} />
                  <EmailVariablesSelector onInsert={handleInsertVariable} />
                </div>
              </div>
              <RichTextEditor
                ref={editorRef}
                value={body}
                onChange={setBody}
                placeholder="Compose your message..."
                className="min-h-[200px] max-h-[300px] overflow-y-auto"
              />
            </div>

            <EmailAttachmentsField
              value={attachments}
              onChange={setAttachments}
              testIdPrefix="confirmation-email-attachments"
            />
          </div>
        )}

        <DialogFooter className="pt-4 flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleClose} data-testid="button-cancel-email">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsScheduleOpen(true)}
            disabled={isSending || !to || !subject || !body}
            data-testid="button-schedule-confirmation"
          >
            <Clock className="h-4 w-4 mr-2" />
            Schedule send
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !to || !subject || !body}
            data-testid="button-send-confirmation"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send Confirmation
          </Button>
        </DialogFooter>
        <ScheduledEmailDialog
          open={isScheduleOpen}
          onOpenChange={setIsScheduleOpen}
          sourceType="one_off"
          recipientSummary={to ? `Schedule confirmation to ${to}` : undefined}
          subjectSummary={subject}
          buildPayload={() => {
            if (!to || !subject || !body) return null;
            return {
              to,
              cc: cc.trim() || undefined,
              subject,
              htmlBody: body,
              attachments:
                attachments.length > 0
                  ? attachments.map((a) => ({
                      filename: a.filename,
                      contentBase64: a.contentBase64,
                      contentType: a.contentType,
                    }))
                  : undefined,
              invoiceId,
            };
          }}
          onScheduled={() => {
            // Close the composer after scheduling so the user lands
            // back on the invoice with a clear "scheduled" toast.
            handleClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
